import { useQuery } from 'react-query'
import { NestAPI, userService } from '@marketing-milk/frontend'
import { userService as adminUserService } from 'app/service/user'
import {
  EntitySearch,
  PaginatedUsersResponse,
  PaginationQueryParams,
  UserWithBusiness,
  UserInvite,
  User,
  LeadSubscription,
} from '@marketing-milk/interfaces'
import { useCallback, useEffect, useState } from 'react'
import { omitBy } from 'lodash'
import _ from 'lodash'

export function useFetchAllUsers() {
  const pageSize = 18
  const [users, setUsers] = useState<UserWithBusiness[]>()
  const [paginationParams, setPaginationParams] = useState<PaginationQueryParams>({
    searchTerm: '',
    entitySearch: EntitySearch.user,
    searchProp: 'firstName',
    sortProp: 'createdAt',
    sortDirection: 'DESC',
    page: 1,
    take: pageSize,
  })

  const stablePaginationSetter = useCallback((updater: any) => {
    setPaginationParams(updater)
  }, [])

  const query = useQuery<PaginatedUsersResponse, string>(
    ['users-paginated', paginationParams],
    () => userService.getAllWithBusinesses(paginationParams),
    {
      keepPreviousData: true,
      staleTime: 5000,
      retry: false,
    }
  )
  const { data } = query

  useEffect(() => {
    if (!data) return
    setUsers(data.users)
  }, [data])

  return {
    users,
    count: data?.count || 0,
    current: data?.users.length,
    pageSize,
    ...paginationParams,
    setPaginationParams: stablePaginationSetter,
    ...query,
  }
}

export function useFetchEmailActivity(userId: number) {
  return useQuery(
    [`${userId}_email_activity`, userId],
    () => userService.getEmailActivity(userId),
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    }
  )
}
export function useFetchLeadSubscriptions(userId: number) {
  const query = useQuery(
    [`${userId}_lead_subscriptions`, userId],
    async () => {
      const resp = await NestAPI.get<{ body: LeadSubscription[] }>(
        `session/lead-subscriptions/for-user/${userId}`
      )
      return resp.data.body
    },
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    }
  )

  return {
    leadSubscriptions: query.data,
    loadingLeadSubscriptions: query.isLoading,
    fetchLeadSubscriptions: query.refetch,
  }
}

export function useFetchEmailDetails(userId: number, messageId: string, enabled: boolean) {
  return useQuery(
    [`${messageId}_email_details`, userId, messageId, enabled],
    () => userService.getEmailDetails(userId, messageId),
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      enabled,
    }
  )
}

export function useGetAuth0User(userId: number, enabled: boolean) {
  return useQuery(
    [`${userId}_auth0_user`, userId, enabled],
    () => userService.getAuth0User(userId),
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      retry: 1,
      enabled,
    }
  )
}

export function useGetMMUser(userId: number, enabled: boolean) {
  const query = useQuery(
    [`${userId}_mm_user`, userId, enabled],
    () => userService.getByID(userId),
    {
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      enabled,
    }
  )
  return {
    loadingMMUser: query.isLoading,
    user: query.data,
    fetchMMUser: query.refetch,
    fetchingMMUser: query.isFetching,
  }
}

export function useFetchUserInvites() {
  const pageSize = 18
  const [invites, setInvites] = useState<UserInvite[]>()
  const [invitedByUsers, setInvitedByUsers] = useState<User[]>([])
  const [paginationParams, setPaginationParams] = useState<PaginationQueryParams>({
    searchTerm: '',
    entitySearch: EntitySearch.user,
    searchProp: 'email',
    sortProp: 'id',
    sortDirection: 'DESC',
    page: 1,
    take: pageSize,
  })

  const stableInvitePaginationSetter = useCallback((updater: any) => {
    setPaginationParams(updater)
  }, [])

  const query = useQuery(
    ['user-invites-paginated', paginationParams],
    () => adminUserService.getInvites({ ...omitBy(paginationParams, _.isUndefined) }),
    {
      keepPreviousData: true,
      staleTime: 5000,
      retry: false,
    }
  )
  const { data } = query

  const invitedByUsersQuery = useQuery(
    ['invited-by-users'],
    () => adminUserService.getInvitedByUsers(),
    {
      keepPreviousData: true,
      staleTime: 60000,
      retry: false,
    }
  )

  useEffect(() => {
    if (!data || !invitedByUsersQuery.data) return
    setInvites(data.invites)
    setInvitedByUsers(invitedByUsersQuery.data)
  }, [data, invitedByUsersQuery.data])

  return {
    invites,
    count: data?.count || 0,
    current: data?.invites?.length || 0,
    pageSize,
    invitedByUsers,
    paginationParams: { ...omitBy(paginationParams, _.isUndefined) },
    setPaginationParams: stableInvitePaginationSetter,
    ...query,
  }
}

export function useDebounce<T>(value: T, delay?: number) {
  const [debouncedValue, setDebouncedValue] = useState<T>(value)
  const [isDebouncing, setIsDebouncing] = useState(false)

  useEffect(() => {
    setIsDebouncing(true)
    const timer = setTimeout(() => {
      setIsDebouncing(false)
      setDebouncedValue(value)
    }, delay || 500)

    return () => {
      clearTimeout(timer)
    }
  }, [value, delay])

  return { debouncedValue, isDebouncing }
}
